"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, Braces, Check, ChevronsUpDown, Eye, ExternalLink, Filter, Gift, Globe, ImagePlus, Info, MessageSquare, Sparkles, Star, Wrench, Zap } from "lucide-react";
import * as React from "react";
import { List, type RowComponentProps } from "react-window";
import { OpenAIMarkIcon } from "@/components/icons/openai-mark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandInput } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFavoriteModels } from "@/hooks/useFavoriteModels";
import { useApi } from "@/lib/fetch-client";
import { formatPrice, formatContextSize, getProviderForModel } from "@/lib/model-utils";
import { cn } from "@/lib/utils";
import { getProviderIcon, providerLogoUrls } from "@llmgateway/shared/components";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { ProviderId } from "@llmgateway/models";
import type { LucideProps } from "lucide-react";
interface ModelSelectorProps {
  models: ApiModel[];
  providers: ApiProvider[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  mode?: "chat" | "video" | "image" | "audio";
  isOptionDisabled?: (value: string) => boolean;
  getOptionDisabledReason?: (value: string) => string | undefined;
  compactTrigger?: boolean;
  allowAutoSelectProvider?: boolean;
  requiresProviderSelection?: boolean;
}
interface FilterState {
  providers: string[];
  capabilities: string[];
  priceRange: "free" | "low" | "medium" | "high" | "all";
  hideUnstable: boolean;
  showOnlyRoot: boolean;
  showFavoritesOnly: boolean;
  showOnlyWithKeys: boolean;
}

// helper to extract simple capability labels from a mapping
function getMappingCapabilities(mapping?: ApiModelProviderMapping, model?: ApiModel): string[] {
  const labels: string[] = [];
  if (mapping) {
    if (mapping.streaming) {
      labels.push("Streaming");
    }
    if (mapping.vision) {
      labels.push("Vision");
    }
    if (mapping.tools) {
      labels.push("Tools");
    }
    if (mapping.reasoning) {
      labels.push("Reasoning");
    }
    if (mapping.webSearch) {
      labels.push("Web Search");
    }
  }

  // Image Generation capability if model outputs include images
  if (model?.output?.includes("image")) {
    labels.push("Image Generation");
  }
  return labels;
}
function getCapabilityIconConfig(capability: string): {
  Icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>> | null;
  color: string;
} {
  switch (capability) {
    case "Streaming":
      return {
        Icon: Zap,
        color: "text-blue-500"
      };
    case "Vision":
      return {
        Icon: Eye,
        color: "text-green-500"
      };
    case "Tools":
      return {
        Icon: Wrench,
        color: "text-purple-500"
      };
    case "Reasoning":
      return {
        Icon: MessageSquare,
        color: "text-orange-500"
      };
    case "JSON Output":
      return {
        Icon: Braces,
        color: "text-cyan-500"
      };
    case "Image Generation":
      return {
        Icon: ImagePlus,
        color: "text-pink-500"
      };
    case "Web Search":
      return {
        Icon: Globe,
        color: "text-teal-500"
      };
    default:
      return {
        Icon: null,
        color: ""
      };
  }
}

// helper to check if a model is unstable or experimental
function isModelUnstable(mapping: ApiModelProviderMapping, model: ApiModel): boolean {
  return [mapping.stability, model.stability].some(stability => stability === "unstable" || stability === "experimental");
}
type PriceField = "input" | "output" | "cachedInput" | "request" | "imageInput" | "imageOutput";
interface MappingPriceInfo {
  label: string;
  original?: string;
  discounted?: string;
}

// Helper to format prices using any provider discount while reusing shared formatPrice logic.
function getMappingPriceInfo(mapping: ApiModelProviderMapping | undefined, field: PriceField): MappingPriceInfo {
  if (!mapping) {
    return {
      label: "Unknown"
    };
  }
  let basePriceStr: string | null | undefined;
  if (field === "input") {
    basePriceStr = mapping.inputPrice;
  } else if (field === "output") {
    basePriceStr = mapping.outputPrice;
  } else if (field === "cachedInput") {
    basePriceStr = mapping.cachedInputPrice;
  } else if (field === "request") {
    basePriceStr = mapping.requestPrice;
  } else if (field === "imageInput") {
    basePriceStr = mapping.imageInputPrice;
  }
  if (basePriceStr === null || basePriceStr === undefined) {
    return {
      label: "Unknown"
    };
  }
  const basePrice = parseFloat(basePriceStr);

  // Free models
  if (basePrice === 0) {
    return {
      label: "Free",
      original: "Free"
    };
  }
  const discountNum = mapping.discount ? parseFloat(mapping.discount) : 0;

  // Request price is a flat per-request fee, not per-token
  if (field === "request") {
    const original = `$${basePrice.toFixed(3)}/req`;
    if (discountNum > 0) {
      const discountedPrice = basePrice * (1 - discountNum);
      const discounted = `$${discountedPrice.toFixed(3)}/req`;
      return {
        label: discounted,
        original,
        discounted
      };
    }
    return {
      label: original,
      original
    };
  }
  const original = formatPrice(basePrice);

  // Apply discount if present
  if (discountNum > 0) {
    const discounted = formatPrice(basePrice * (1 - discountNum));
    return {
      label: discounted,
      original,
      discounted
    };
  }
  return {
    label: original,
    original
  };
}
interface RootAggregateInfo {
  minInputPrice?: number;
  minOutputPrice?: number;
  minCachedInputPrice?: number;
  minRequestPrice?: number;
  minImageInputPrice?: number;
  minImageOutputPrice?: number;
  maxContextSize?: number;
  maxOutput?: number;
  capabilities: string[];
}
function getRootAggregateInfo(model: ApiModel): RootAggregateInfo {
  const now = new Date();
  let minInputPrice: number | undefined;
  let minOutputPrice: number | undefined;
  let minCachedInputPrice: number | undefined;
  let minRequestPrice: number | undefined;
  let minImageInputPrice: number | undefined;
  let minImageOutputPrice: number | undefined;
  let maxContextSize: number | undefined;
  let maxOutput: number | undefined;
  const capabilitySet = new Set<string>();
  const applyDiscount = (priceStr: string | null | undefined, discountStr?: string | null) => {
    if (priceStr === null || priceStr === undefined) {
      return undefined;
    }
    const price = parseFloat(priceStr);
    if (price === 0) {
      return 0;
    }
    const discount = discountStr ? parseFloat(discountStr) : 0;
    if (!discount || discount <= 0) {
      return price;
    }
    return price * (1 - discount);
  };
  for (const mapping of model.mappings) {
    // Skip deactivated providers when computing "best" supported values
    const isDeactivated = mapping.deactivatedAt && new Date(mapping.deactivatedAt) <= now;
    if (isDeactivated) {
      continue;
    }
    const effectiveInput = applyDiscount(mapping.inputPrice, mapping.discount);
    if (effectiveInput !== undefined && (minInputPrice === undefined || effectiveInput < minInputPrice)) {
      minInputPrice = effectiveInput;
    }
    const effectiveOutput = applyDiscount(mapping.outputPrice, mapping.discount);
    if (effectiveOutput !== undefined && (minOutputPrice === undefined || effectiveOutput < minOutputPrice)) {
      minOutputPrice = effectiveOutput;
    }
    const effectiveCached = applyDiscount(mapping.cachedInputPrice, mapping.discount);
    if (effectiveCached !== undefined && (minCachedInputPrice === undefined || effectiveCached < minCachedInputPrice)) {
      minCachedInputPrice = effectiveCached;
    }

    // Track image generation pricing
    const effectiveRequest = applyDiscount(mapping.requestPrice, mapping.discount);
    if (effectiveRequest !== undefined && (minRequestPrice === undefined || effectiveRequest < minRequestPrice)) {
      minRequestPrice = effectiveRequest;
    }
    const effectiveImageInput = applyDiscount(mapping.imageInputPrice, mapping.discount);
    if (effectiveImageInput !== undefined && (minImageInputPrice === undefined || effectiveImageInput < minImageInputPrice)) {
      minImageInputPrice = effectiveImageInput;
    }
    if (mapping.contextSize !== null && mapping.contextSize !== undefined && (maxContextSize === undefined || mapping.contextSize > maxContextSize)) {
      maxContextSize = mapping.contextSize;
    }
    if (mapping.maxOutput !== null && mapping.maxOutput !== undefined && (maxOutput === undefined || mapping.maxOutput > maxOutput)) {
      maxOutput = mapping.maxOutput;
    }
    getMappingCapabilities(mapping, model).forEach(capability => capabilitySet.add(capability));
  }

  // Ensure image generation capability is included if any provider supports image output
  if (model.output?.includes("image")) {
    capabilitySet.add("Image Generation");
  }
  return {
    minInputPrice,
    minOutputPrice,
    minCachedInputPrice,
    minRequestPrice,
    minImageInputPrice,
    minImageOutputPrice,
    maxContextSize,
    maxOutput,
    capabilities: Array.from(capabilitySet)
  };
}

// Removed old ModelItem; we render entries per provider below

function formatPerSecondPrice(perSecondPrice: Record<string, string>): string {
  const defaultAudio = perSecondPrice["default_audio"];
  const defaultVideo = perSecondPrice["default_video"];
  const defaultPrice = perSecondPrice["default"];
  if (defaultAudio && defaultVideo) {
    return `$${defaultVideo} – $${defaultAudio}/sec`;
  }
  if (defaultPrice) {
    return `$${defaultPrice}/sec`;
  }
  const firstValue = Object.values(perSecondPrice)[0];
  return firstValue ? `$${firstValue}/sec` : "Unknown";
}

// Estimate cost for generating one typical 1K/1024x1024 image based on
// request fee + image output tokens * price.
function estimateImageCost(mapping: ApiModelProviderMapping | undefined): {
  base: number;
  discounted: number;
} | null {
  if (!mapping) {
    return null;
  }
  const request = mapping.requestPrice ? parseFloat(mapping.requestPrice) : 0;
  const discount = mapping.discount ? parseFloat(mapping.discount) : 0;
  const imageOut = mapping.imageOutputPrice ? parseFloat(mapping.imageOutputPrice) : 0;
  const tokensMap = mapping.imageOutputTokensByResolution;
  const tokens = tokensMap?.default ?? tokensMap?.["1024x1024"] ?? tokensMap?.["1K"];
  if (tokens === undefined) {
    return null;
  }
  const outputCost = imageOut * tokens;
  const base = request + outputCost;
  if (!Number.isFinite(base) || base <= 0) {
    return null;
  }
  const discounted = discount > 0 ? base * (1 - discount) : base;
  return {
    base,
    discounted
  };
}
function getMinImageCostEstimate(mappings: ApiModelProviderMapping[]): {
  base: number;
  discounted: number;
} | null {
  const now = new Date();
  let best: {
    base: number;
    discounted: number;
  } | null = null;
  for (const m of mappings) {
    const isDeactivated = m.deactivatedAt && new Date(m.deactivatedAt) <= now;
    if (isDeactivated) {
      continue;
    }
    const est = estimateImageCost(m);
    if (!est) {
      continue;
    }
    if (!best || est.discounted < best.discounted) {
      best = est;
    }
  }
  return best;
}
function formatImageCost(cost: number): string {
  if (cost >= 0.01) {
    return `$${cost.toFixed(3)}`;
  }
  if (cost >= 0.001) {
    return `$${cost.toFixed(4)}`;
  }
  return `$${cost.toFixed(5)}`;
}
function ImageEstimateCard(t0) {
  const $ = _c(13);
  const {
    estimate,
    labelClassName,
    valueClassName,
    captionClassName
  } = t0;
  const t1 = labelClassName ?? "text-[11px] font-medium text-muted-foreground uppercase tracking-wide";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className={t1}>Est. per Image</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = valueClassName ?? "text-sm font-mono font-semibold text-primary";
  let t4;
  if ($[2] !== estimate.discounted) {
    t4 = formatImageCost(estimate.discounted);
    $[2] = estimate.discounted;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <p className={t3}>~{t4}</p>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = captionClassName ?? "text-[10px] text-muted-foreground";
  let t7;
  if ($[7] !== t6) {
    t7 = <p className={t6}>Typical 1024×1024 output</p>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t5 || $[11] !== t7) {
    t8 = <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5">{t2}{t5}{t7}</div>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function getMinPerSecondPrice(mappings: ApiModelProviderMapping[]): string | null {
  let min: number | null = null;
  for (const m of mappings) {
    if (!m.perSecondPrice) {
      continue;
    }
    for (const v of Object.values(m.perSecondPrice)) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && (min === null || n < min)) {
        min = n;
      }
    }
  }
  return min !== null ? `$${min}/sec` : null;
}
function getMaxPerSecondPrice(mappings: ApiModelProviderMapping[]): string | null {
  let max: number | null = null;
  for (const m of mappings) {
    if (!m.perSecondPrice) {
      continue;
    }
    for (const v of Object.values(m.perSecondPrice)) {
      const n = parseFloat(v);
      if (Number.isFinite(n) && (max === null || n > max)) {
        max = n;
      }
    }
  }
  return max !== null ? `$${max}/sec` : null;
}
interface ModelEntry {
  model: ApiModel;
  mapping?: ApiModelProviderMapping;
  provider?: ApiProvider;
  isRoot?: boolean;
  searchText: string;
}
const ROW_HEIGHT = 56;
interface ModelEntryRowProps {
  entries: ModelEntry[];
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  selectedEntryKey: string;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  onSelect: (value: string) => void;
  setPreviewEntry: (entry: {
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
    isRoot?: boolean;
  } | null) => void;
  setSelectedDetails: (details: {
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
  } | null) => void;
  setDetailsOpen: (open: boolean) => void;
  isOptionDisabled?: (value: string) => boolean;
  getOptionDisabledReason?: (value: string) => string | undefined;
}
function ModelEntryRowComponent(t0) {
  const $ = _c(181);
  const {
    ariaAttributes,
    index,
    style,
    entries,
    focusedIndex,
    setFocusedIndex,
    selectedEntryKey,
    isFavorite,
    toggleFavorite,
    onSelect,
    setPreviewEntry,
    setSelectedDetails,
    setDetailsOpen,
    isOptionDisabled,
    getOptionDisabledReason
  } = t0;
  const entry = entries[index];
  if (!entry) {
    return null;
  }
  const {
    model,
    mapping,
    provider,
    isRoot
  } = entry;
  const isFocused = index === focusedIndex;
  if (isRoot) {
    const entryKey = model.id;
    let t1;
    if ($[0] !== entryKey || $[1] !== isOptionDisabled) {
      t1 = isOptionDisabled?.(entryKey) ?? false;
      $[0] = entryKey;
      $[1] = isOptionDisabled;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const disabled = t1;
    let t2;
    if ($[3] !== entryKey || $[4] !== getOptionDisabledReason) {
      t2 = getOptionDisabledReason?.(entryKey);
      $[3] = entryKey;
      $[4] = getOptionDisabledReason;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    const disabledReason = t2;
    let t3;
    if ($[6] !== model.mappings) {
      t3 = model.mappings.some(_temp);
      $[6] = model.mappings;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const hasRequestPrice = t3;
    let t4;
    if ($[8] !== hasRequestPrice || $[9] !== model.free || $[10] !== model.mappings) {
      t4 = model.free === true && !hasRequestPrice && model.mappings.every(_temp2);
      $[8] = hasRequestPrice;
      $[9] = model.free;
      $[10] = model.mappings;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    const isFreeRoot = t4;
    let t5;
    if ($[12] !== index || $[13] !== isRoot || $[14] !== mapping || $[15] !== model || $[16] !== provider || $[17] !== setFocusedIndex || $[18] !== setPreviewEntry) {
      t5 = () => {
        setFocusedIndex(index);
        setPreviewEntry({
          model,
          mapping,
          provider,
          isRoot
        });
      };
      $[12] = index;
      $[13] = isRoot;
      $[14] = mapping;
      $[15] = model;
      $[16] = provider;
      $[17] = setFocusedIndex;
      $[18] = setPreviewEntry;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== disabled || $[21] !== model.id || $[22] !== onSelect) {
      t6 = () => {
        if (disabled) {
          return;
        }
        onSelect(model.id);
      };
      $[20] = disabled;
      $[21] = model.id;
      $[22] = onSelect;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    const t7 = isFocused ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground";
    const t8 = disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";
    let t9;
    if ($[24] !== t7 || $[25] !== t8) {
      t9 = cn("relative flex h-full select-none items-center gap-2 rounded-[14px] px-2 text-sm outline-none", t7, t8);
      $[24] = t7;
      $[25] = t8;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    const t10 = entryKey === selectedEntryKey ? "opacity-100" : "opacity-0";
    let t11;
    if ($[27] !== t10) {
      t11 = cn("h-4 w-4 shrink-0", t10);
      $[27] = t10;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== t11) {
      t12 = <Check className={t11} />;
      $[29] = t11;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    let t13;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="flex h-5 w-6 shrink-0 items-center justify-center"><Sparkles className="h-5 w-5 text-primary" /></div>;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== model.name) {
      t14 = <span className="font-medium truncate">{model.name}</span>;
      $[32] = model.name;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== isFreeRoot) {
      t15 = isFreeRoot && <Gift className="h-3.5 w-3.5 shrink-0 text-emerald-500" />;
      $[34] = isFreeRoot;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== t14 || $[37] !== t15) {
      t16 = <div className="flex items-center gap-1 min-w-0">{t14}{t15}</div>;
      $[36] = t14;
      $[37] = t15;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="text-xs text-muted-foreground truncate">Auto-select provider</span>;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== t16) {
      t18 = <div className="flex items-center gap-2 min-w-0 flex-1">{t13}<div className="flex flex-col min-w-0 flex-1">{t16}{t17}</div></div>;
      $[40] = t16;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    const t19 = isFavorite(model.id) ? "Remove from favorites" : "Add to favorites";
    let t20;
    if ($[42] !== isFavorite || $[43] !== model.id) {
      t20 = isFavorite(model.id);
      $[42] = isFavorite;
      $[43] = model.id;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    let t21;
    if ($[45] !== model.id || $[46] !== toggleFavorite) {
      t21 = e => {
        e.stopPropagation();
        toggleFavorite(model.id);
      };
      $[45] = model.id;
      $[46] = toggleFavorite;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    let t22;
    if ($[48] !== isFavorite || $[49] !== model.id) {
      t22 = isFavorite(model.id) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground";
      $[48] = isFavorite;
      $[49] = model.id;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    let t23;
    if ($[51] !== t22) {
      t23 = cn("h-4 w-4", t22);
      $[51] = t22;
      $[52] = t23;
    } else {
      t23 = $[52];
    }
    let t24;
    if ($[53] !== t23) {
      t24 = <Star className={t23} />;
      $[53] = t23;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] !== t19 || $[56] !== t20 || $[57] !== t21 || $[58] !== t24) {
      t25 = <Button variant="ghost" size="sm" aria-label={t19} aria-pressed={t20} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0" onClick={t21}>{t24}</Button>;
      $[55] = t19;
      $[56] = t20;
      $[57] = t21;
      $[58] = t24;
      $[59] = t25;
    } else {
      t25 = $[59];
    }
    let t26;
    if ($[60] !== model || $[61] !== setDetailsOpen || $[62] !== setSelectedDetails) {
      t26 = e_0 => {
        e_0.stopPropagation();
        setSelectedDetails({
          model
        });
        setDetailsOpen(true);
      };
      $[60] = model;
      $[61] = setDetailsOpen;
      $[62] = setSelectedDetails;
      $[63] = t26;
    } else {
      t26 = $[63];
    }
    let t27;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <Info className="h-4 w-4" />;
      $[64] = t27;
    } else {
      t27 = $[64];
    }
    let t28;
    if ($[65] !== t26) {
      t28 = <Button variant="ghost" size="sm" aria-label="Model details" className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0 md:hidden" onClick={t26}>{t27}</Button>;
      $[65] = t26;
      $[66] = t28;
    } else {
      t28 = $[66];
    }
    let t29;
    if ($[67] !== t18 || $[68] !== t25 || $[69] !== t28) {
      t29 = <div className="flex items-center justify-between flex-1 min-w-0 gap-2">{t18}{t25}{t28}</div>;
      $[67] = t18;
      $[68] = t25;
      $[69] = t28;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    let t30;
    if ($[71] !== disabledReason || $[72] !== t12 || $[73] !== t29 || $[74] !== t5 || $[75] !== t6 || $[76] !== t9) {
      t30 = <div title={disabledReason} onMouseEnter={t5} onClick={t6} className={t9}>{t12}{t29}</div>;
      $[71] = disabledReason;
      $[72] = t12;
      $[73] = t29;
      $[74] = t5;
      $[75] = t6;
      $[76] = t9;
      $[77] = t30;
    } else {
      t30 = $[77];
    }
    let t31;
    if ($[78] !== ariaAttributes || $[79] !== style || $[80] !== t30) {
      t31 = <div {...ariaAttributes} style={style} className="px-1 py-0.5">{t30}</div>;
      $[78] = ariaAttributes;
      $[79] = style;
      $[80] = t30;
      $[81] = t31;
    } else {
      t31 = $[81];
    }
    return t31;
  }
  let t1;
  if ($[82] !== provider) {
    t1 = provider ? provider.id === "openai" ? OpenAIMarkIcon : getProviderIcon(provider.id) : null;
    $[82] = provider;
    $[83] = t1;
  } else {
    t1 = $[83];
  }
  const ProviderIcon = t1;
  const entryKey_0 = `${mapping.providerId}-${model.id}-${mapping.region ?? ""}`;
  const providerModelValue = `${mapping.providerId}/${model.id}${mapping.region ? `:${mapping.region}` : ""}`;
  let t2;
  if ($[84] !== isOptionDisabled || $[85] !== providerModelValue) {
    t2 = isOptionDisabled?.(providerModelValue) ?? false;
    $[84] = isOptionDisabled;
    $[85] = providerModelValue;
    $[86] = t2;
  } else {
    t2 = $[86];
  }
  const disabled_0 = t2;
  let t3;
  if ($[87] !== getOptionDisabledReason || $[88] !== providerModelValue) {
    t3 = getOptionDisabledReason?.(providerModelValue);
    $[87] = getOptionDisabledReason;
    $[88] = providerModelValue;
    $[89] = t3;
  } else {
    t3 = $[89];
  }
  const disabledReason_0 = t3;
  let t4;
  if ($[90] !== mapping || $[91] !== model) {
    t4 = isModelUnstable(mapping, model);
    $[90] = mapping;
    $[91] = model;
    $[92] = t4;
  } else {
    t4 = $[92];
  }
  const isUnstable = t4;
  let t5;
  if ($[93] !== mapping.deprecatedAt) {
    t5 = mapping.deprecatedAt && new Date(mapping.deprecatedAt) <= new Date();
    $[93] = mapping.deprecatedAt;
    $[94] = t5;
  } else {
    t5 = $[94];
  }
  const isDeprecated = t5;
  const hasRequestPrice_0 = mapping.requestPrice && parseFloat(mapping.requestPrice) > 0;
  const isFreeMapping = model.free === true && !hasRequestPrice_0 && (!mapping.inputPrice || parseFloat(mapping.inputPrice) === 0) && (!mapping.outputPrice || parseFloat(mapping.outputPrice) === 0);
  let t6;
  if ($[95] !== index || $[96] !== isRoot || $[97] !== mapping || $[98] !== model || $[99] !== provider || $[100] !== setFocusedIndex || $[101] !== setPreviewEntry) {
    t6 = () => {
      setFocusedIndex(index);
      setPreviewEntry({
        model,
        mapping,
        provider,
        isRoot
      });
    };
    $[95] = index;
    $[96] = isRoot;
    $[97] = mapping;
    $[98] = model;
    $[99] = provider;
    $[100] = setFocusedIndex;
    $[101] = setPreviewEntry;
    $[102] = t6;
  } else {
    t6 = $[102];
  }
  let t7;
  if ($[103] !== disabled_0 || $[104] !== onSelect || $[105] !== providerModelValue) {
    t7 = () => {
      if (disabled_0) {
        return;
      }
      onSelect(providerModelValue);
    };
    $[103] = disabled_0;
    $[104] = onSelect;
    $[105] = providerModelValue;
    $[106] = t7;
  } else {
    t7 = $[106];
  }
  const t8 = isFocused ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground";
  const t9 = disabled_0 ? "cursor-not-allowed opacity-50" : "cursor-pointer";
  let t10;
  if ($[107] !== t8 || $[108] !== t9) {
    t10 = cn("relative flex h-full select-none items-center gap-2 rounded-[14px] px-2 text-sm outline-none", t8, t9);
    $[107] = t8;
    $[108] = t9;
    $[109] = t10;
  } else {
    t10 = $[109];
  }
  const t11 = entryKey_0 === selectedEntryKey ? "opacity-100" : "opacity-0";
  let t12;
  if ($[110] !== t11) {
    t12 = cn("h-4 w-4 shrink-0", t11);
    $[110] = t11;
    $[111] = t12;
  } else {
    t12 = $[111];
  }
  let t13;
  if ($[112] !== t12) {
    t13 = <Check className={t12} />;
    $[112] = t12;
    $[113] = t13;
  } else {
    t13 = $[113];
  }
  let t14;
  if ($[114] !== ProviderIcon) {
    t14 = ProviderIcon ? <div className="flex h-5 w-6 shrink-0 items-center justify-center"><ProviderIcon className="h-5 w-auto max-w-6 shrink-0 object-contain dark:text-white" /></div> : null;
    $[114] = ProviderIcon;
    $[115] = t14;
  } else {
    t14 = $[115];
  }
  let t15;
  if ($[116] !== model.name) {
    t15 = <span className="font-medium truncate">{model.name}</span>;
    $[116] = model.name;
    $[117] = t15;
  } else {
    t15 = $[117];
  }
  let t16;
  if ($[118] !== isFreeMapping) {
    t16 = isFreeMapping && <Gift className="h-3.5 w-3.5 shrink-0 text-emerald-500" />;
    $[118] = isFreeMapping;
    $[119] = t16;
  } else {
    t16 = $[119];
  }
  let t17;
  if ($[120] !== isDeprecated || $[121] !== isUnstable) {
    t17 = (isUnstable || isDeprecated) && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-500" />;
    $[120] = isDeprecated;
    $[121] = isUnstable;
    $[122] = t17;
  } else {
    t17 = $[122];
  }
  let t18;
  if ($[123] !== t15 || $[124] !== t16 || $[125] !== t17) {
    t18 = <div className="flex items-center gap-1 min-w-0">{t15}{t16}{t17}</div>;
    $[123] = t15;
    $[124] = t16;
    $[125] = t17;
    $[126] = t18;
  } else {
    t18 = $[126];
  }
  const t19 = disabledReason_0 ?? provider?.name;
  let t20;
  if ($[127] !== disabledReason_0 || $[128] !== mapping.region) {
    t20 = !disabledReason_0 && mapping?.region && <span className="ml-1">({mapping.region})</span>;
    $[127] = disabledReason_0;
    $[128] = mapping.region;
    $[129] = t20;
  } else {
    t20 = $[129];
  }
  let t21;
  if ($[130] !== t19 || $[131] !== t20) {
    t21 = <span className="text-xs text-muted-foreground truncate">{t19}{t20}</span>;
    $[130] = t19;
    $[131] = t20;
    $[132] = t21;
  } else {
    t21 = $[132];
  }
  let t22;
  if ($[133] !== t18 || $[134] !== t21) {
    t22 = <div className="flex flex-col min-w-0 flex-1">{t18}{t21}</div>;
    $[133] = t18;
    $[134] = t21;
    $[135] = t22;
  } else {
    t22 = $[135];
  }
  let t23;
  if ($[136] !== t14 || $[137] !== t22) {
    t23 = <div className="flex items-center gap-2 min-w-0 flex-1">{t14}{t22}</div>;
    $[136] = t14;
    $[137] = t22;
    $[138] = t23;
  } else {
    t23 = $[138];
  }
  const t24 = isFavorite(providerModelValue) ? "Remove from favorites" : "Add to favorites";
  let t25;
  if ($[139] !== isFavorite || $[140] !== providerModelValue) {
    t25 = isFavorite(providerModelValue);
    $[139] = isFavorite;
    $[140] = providerModelValue;
    $[141] = t25;
  } else {
    t25 = $[141];
  }
  let t26;
  if ($[142] !== providerModelValue || $[143] !== toggleFavorite) {
    t26 = e_1 => {
      e_1.stopPropagation();
      toggleFavorite(providerModelValue);
    };
    $[142] = providerModelValue;
    $[143] = toggleFavorite;
    $[144] = t26;
  } else {
    t26 = $[144];
  }
  let t27;
  if ($[145] !== isFavorite || $[146] !== providerModelValue) {
    t27 = isFavorite(providerModelValue) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground";
    $[145] = isFavorite;
    $[146] = providerModelValue;
    $[147] = t27;
  } else {
    t27 = $[147];
  }
  let t28;
  if ($[148] !== t27) {
    t28 = cn("h-4 w-4", t27);
    $[148] = t27;
    $[149] = t28;
  } else {
    t28 = $[149];
  }
  let t29;
  if ($[150] !== t28) {
    t29 = <Star className={t28} />;
    $[150] = t28;
    $[151] = t29;
  } else {
    t29 = $[151];
  }
  let t30;
  if ($[152] !== t24 || $[153] !== t25 || $[154] !== t26 || $[155] !== t29) {
    t30 = <Button variant="ghost" size="sm" aria-label={t24} aria-pressed={t25} className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0" onClick={t26}>{t29}</Button>;
    $[152] = t24;
    $[153] = t25;
    $[154] = t26;
    $[155] = t29;
    $[156] = t30;
  } else {
    t30 = $[156];
  }
  let t31;
  if ($[157] !== mapping || $[158] !== model || $[159] !== provider || $[160] !== setDetailsOpen || $[161] !== setSelectedDetails) {
    t31 = e_2 => {
      e_2.stopPropagation();
      setSelectedDetails({
        model,
        mapping,
        provider
      });
      setDetailsOpen(true);
    };
    $[157] = mapping;
    $[158] = model;
    $[159] = provider;
    $[160] = setDetailsOpen;
    $[161] = setSelectedDetails;
    $[162] = t31;
  } else {
    t31 = $[162];
  }
  let t32;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Info className="h-4 w-4" />;
    $[163] = t32;
  } else {
    t32 = $[163];
  }
  let t33;
  if ($[164] !== t31) {
    t33 = <Button variant="ghost" size="sm" aria-label="Model details" className="h-8 w-8 p-0 hover:bg-muted/50 shrink-0 md:hidden" onClick={t31}>{t32}</Button>;
    $[164] = t31;
    $[165] = t33;
  } else {
    t33 = $[165];
  }
  let t34;
  if ($[166] !== t23 || $[167] !== t30 || $[168] !== t33) {
    t34 = <div className="flex items-center justify-between flex-1 min-w-0 gap-2">{t23}{t30}{t33}</div>;
    $[166] = t23;
    $[167] = t30;
    $[168] = t33;
    $[169] = t34;
  } else {
    t34 = $[169];
  }
  let t35;
  if ($[170] !== disabledReason_0 || $[171] !== t10 || $[172] !== t13 || $[173] !== t34 || $[174] !== t6 || $[175] !== t7) {
    t35 = <div title={disabledReason_0} onMouseEnter={t6} onClick={t7} className={t10}>{t13}{t34}</div>;
    $[170] = disabledReason_0;
    $[171] = t10;
    $[172] = t13;
    $[173] = t34;
    $[174] = t6;
    $[175] = t7;
    $[176] = t35;
  } else {
    t35 = $[176];
  }
  let t36;
  if ($[177] !== ariaAttributes || $[178] !== style || $[179] !== t35) {
    t36 = <div {...ariaAttributes} style={style} className="px-1 py-0.5">{t35}</div>;
    $[177] = ariaAttributes;
    $[178] = style;
    $[179] = t35;
    $[180] = t36;
  } else {
    t36 = $[180];
  }
  return t36;
}
function _temp2(p_0) {
  return (!p_0.inputPrice || parseFloat(p_0.inputPrice) === 0) && (!p_0.outputPrice || parseFloat(p_0.outputPrice) === 0);
}
function _temp(p) {
  return p.requestPrice && parseFloat(p.requestPrice) > 0;
}
export function ModelSelector({
  models,
  providers,
  value,
  onValueChange,
  placeholder = "Select model...",
  mode = "chat",
  isOptionDisabled,
  getOptionDisabledReason,
  compactTrigger = false,
  allowAutoSelectProvider = true,
  requiresProviderSelection = false
}: ModelSelectorProps) {
  const {
    isFavorite,
    toggleFavorite
  } = useFavoriteModels();
  const isMobile = useIsMobile();
  const api = useApi();
  const {
    data: providerKeysData
  } = api.useQuery("get", "/keys/provider/active");
  const providersWithKeys = React.useMemo(() => {
    const keys = providerKeysData?.providerKeys ?? [];
    return new Set(keys.filter(k => k.status === "active").map(k_0 => k_0.provider as string));
  }, [providerKeysData]);
  const [open, setOpen] = React.useState(false);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const listContainerRef = React.useRef<HTMLDivElement>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedDetails, setSelectedDetails] = React.useState<{
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
  } | null>(null);
  const [previewEntry, setPreviewEntry] = React.useState<{
    model: ApiModel;
    mapping?: ApiModelProviderMapping;
    provider?: ApiProvider;
    isRoot?: boolean;
  } | null>(null);
  const [filters, setFilters] = React.useState<FilterState>({
    providers: [],
    capabilities: [],
    priceRange: "all",
    hideUnstable: true,
    showOnlyRoot: false,
    showFavoritesOnly: false,
    showOnlyWithKeys: false
  });
  const [previewExpandTokens, setPreviewExpandTokens] = React.useState(false);
  const [selectedExpandTokens, setSelectedExpandTokens] = React.useState(false);
  const isImageGenModel = (model?: ApiModel | null) => !!model?.output && model.output.includes("image");
  const previewIsImageGen = isImageGenModel(previewEntry?.model);
  const selectedIsImageGen = isImageGenModel(selectedDetails?.model);
  React.useEffect(() => {
    setPreviewExpandTokens(false);
  }, [previewEntry?.model]);
  React.useEffect(() => {
    setSelectedExpandTokens(false);
  }, [selectedDetails?.model]);

  // Parse value as provider/model-id (preferred). Fallback to model id only.
  // Supports region suffix: "alibaba/deepseek-v3.2:cn-beijing" or
  // "aws-bedrock/claude-haiku-4-5:global". The region is the substring after
  // the last ":" (model.id never contains ":"; upstream modelNames may).
  const raw = value ?? "";
  const [selectedProviderId, selectedModelIdRaw] = raw.includes("/") ? raw.split("/") as [string, string] : ["", raw];
  // model.id never contains ":", so split on ":" is safe for the URL form
  // (provider/model.id[:region]). Look up by canonical (root) id only.
  const lastColonIdx = selectedModelIdRaw.lastIndexOf(":");
  const selectedRegion = lastColonIdx > -1 ? selectedModelIdRaw.slice(lastColonIdx + 1) : undefined;
  const selectedModelId = lastColonIdx > -1 ? selectedModelIdRaw.slice(0, lastColonIdx) : selectedModelIdRaw;
  const selectedModel = models.find(m => m.id === selectedModelId);
  const selectedProviderDef = providers.find(p => p.id === selectedProviderId);
  // Strict (providerId, region) match — no loose fallbacks that would pick
  // the first regional variant when no region was requested.
  const selectedMapping = selectedRegion ? selectedModel?.mappings.find(p_0 => p_0.providerId === selectedProviderId && p_0.region === selectedRegion) : selectedModel?.mappings.find(p_1 => p_1.providerId === selectedProviderId && !p_1.region);
  const selectedEntryKey = selectedModel && selectedProviderId && selectedMapping ? `${selectedProviderId}-${selectedModel.id}-${selectedMapping.region ?? ""}` : selectedModel ? selectedModel.id : "";

  // Simple normalizer for search matching
  const normalize = (s: string) => s.toLowerCase().replace(/[-_\s]+/g, "");

  // Build entries of model per provider mapping (include all, filter later)
  const allEntries = React.useMemo(() => {
    const out: {
      model: ApiModel;
      mapping?: ApiModelProviderMapping;
      provider?: ApiProvider;
      isRoot?: boolean;
      searchText: string;
    }[] = [];
    const now = new Date();

    // Sort by public release date first so newly released models surface
    // before older models that were added to ChatApp more recently.
    const sortedModels = [...models].sort((a, b) => {
      const dateA = a.releasedAt ? new Date(a.releasedAt).getTime() : a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.releasedAt ? new Date(b.releasedAt).getTime() : b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });

    // Always place the "auto" model at the very top
    const autoModel = sortedModels.find(m_0 => m_0.id === "auto");
    if (allowAutoSelectProvider && autoModel) {
      out.push({
        model: autoModel,
        isRoot: true,
        searchText: normalize([autoModel.name ?? "", autoModel.family ?? "", autoModel.id, autoModel.aliases?.join(" ") ?? ""].join(" "))
      });
    }
    for (const m_1 of sortedModels) {
      if (m_1.id === "custom" || m_1.id === "auto") {
        continue;
      }

      // Add root model entry (auto-routing)
      const aliasText = m_1.aliases?.join(" ") ?? "";
      const rootSearchText = normalize([m_1.name ?? "", m_1.family ?? "", m_1.id, aliasText].join(" "));
      if (allowAutoSelectProvider) {
        out.push({
          model: m_1,
          isRoot: true,
          searchText: rootSearchText
        });
      }
      for (const mp of m_1.mappings) {
        const isDeactivated = mp.deactivatedAt && new Date(mp.deactivatedAt) <= now;
        if (!isDeactivated) {
          const provider = providers.find(p_2 => p_2.id === mp.providerId);
          const searchText = normalize([m_1.name ?? "", m_1.family ?? "", m_1.id, provider?.name ?? "", aliasText].join(" "));
          out.push({
            model: m_1,
            mapping: mp,
            provider,
            isRoot: false,
            searchText
          });
        }
      }
    }
    return out;
  }, [models, providers, allowAutoSelectProvider]);

  // Defer search input value to keep typing responsive with large lists
  const deferredSearch = React.useDeferredValue(searchQuery);

  // Get unique providers and capabilities for filtering
  const availableProviders = React.useMemo(() => {
    const ids = new Set(allEntries.filter(e => e.mapping).map(e_0 => e_0.mapping!.providerId));
    return providers.filter(p_3 => ids.has(p_3.id as any));
  }, [allEntries, providers]);
  const availableCapabilities = React.useMemo(() => {
    const set = new Set<string>();
    allEntries.forEach(e_1 => getMappingCapabilities(e_1.mapping, e_1.model).forEach(c => set.add(c)));
    return Array.from(set).sort();
  }, [allEntries]);
  const filteredEntries = React.useMemo(() => {
    let list = allEntries;
    if (filters.showOnlyRoot) {
      list = list.filter(e_2 => e_2.isRoot);
    }
    if (filters.hideUnstable) {
      list = list.filter(e_3 => {
        // Root models are considered stable unless model itself is unstable
        if (e_3.isRoot) {
          return e_3.model.stability !== "unstable" && e_3.model.stability !== "experimental";
        }
        return e_3.mapping?.stability !== "unstable" && e_3.mapping?.stability !== "experimental" && e_3.model.stability !== "unstable" && e_3.model.stability !== "experimental";
      });
    }
    if (deferredSearch) {
      const tokens = deferredSearch.toLowerCase().split(/[-_\s]+/).filter(Boolean);
      if (tokens.length > 0) {
        list = list.filter(entry => tokens.every(t => entry.searchText.includes(t)));
      }
    }
    if (filters.providers.length > 0) {
      list = list.filter(e_4 => e_4.mapping && filters.providers.includes(e_4.mapping.providerId));
    }
    if (filters.capabilities.length > 0) {
      list = list.filter(e_5 => {
        const caps = getMappingCapabilities(e_5.mapping, e_5.model);
        return filters.capabilities.every(c_0 => caps.includes(c_0));
      });
    }
    if (filters.priceRange !== "all") {
      list = list.filter(e_6 => {
        // Root models don't have fixed price, exclude from price filter or include?
        // Let's exclude them if filtering by price, or maybe assume 'free' if unknown?
        // Safest is to only filter items that have a mapping.
        if (!e_6.mapping) {
          return false;
        }
        const price = e_6.mapping.inputPrice ? parseFloat(e_6.mapping.inputPrice) : 0;
        const requestPrice = e_6.mapping.requestPrice ? parseFloat(e_6.mapping.requestPrice) : 0;
        switch (filters.priceRange) {
          case "free":
            return e_6.model.free === true && price === 0 && requestPrice === 0;
          case "low":
            return price > 0 && price <= 0.000001;
          case "medium":
            return price > 0.000001 && price <= 0.00001;
          case "high":
            return price > 0.00001;
          default:
            return true;
        }
      });
    }
    if (filters.showFavoritesOnly) {
      list = list.filter(e_7 => {
        if (e_7.isRoot) {
          return isFavorite(e_7.model.id);
        }
        const mappingId = e_7.mapping ? `${e_7.mapping.providerId}/${e_7.model.id}${e_7.mapping.region ? `:${e_7.mapping.region}` : ""}` : null;
        return mappingId !== null && isFavorite(mappingId);
      });
    }
    if (filters.showOnlyWithKeys) {
      const now_0 = new Date();
      list = list.filter(e_8 => {
        if (e_8.isRoot) {
          return e_8.model.mappings?.some(m_2 => providersWithKeys.has(m_2.providerId) && !(m_2.deactivatedAt && new Date(m_2.deactivatedAt) <= now_0));
        }
        return e_8.mapping ? providersWithKeys.has(e_8.mapping.providerId) : false;
      });
    }
    if (deferredSearch) {
      const tokens_0 = deferredSearch.toLowerCase().split(/[-_\s]+/).filter(Boolean);
      if (tokens_0.length > 0) {
        list = [...list].sort((a_0, b_0) => {
          if (a_0.isRoot === b_0.isRoot) {
            return 0;
          }
          return a_0.isRoot ? -1 : 1;
        });
      }
    }
    return list;
  }, [allEntries, deferredSearch, filters, isFavorite, providersWithKeys]);
  const updateFilter = (key: keyof FilterState, value_0: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value_0
    }));
  };
  const toggleProviderFilter = (providerId: string) => {
    setFilters(prev_0 => ({
      ...prev_0,
      providers: prev_0.providers.includes(providerId) ? prev_0.providers.filter(id => id !== providerId) : [...prev_0.providers, providerId],
      // If selecting providers, we probably don't want to show root models only
      showOnlyRoot: false
    }));
  };
  const toggleCapabilityFilter = (capability: string) => {
    setFilters(prev_1 => ({
      ...prev_1,
      capabilities: prev_1.capabilities.includes(capability) ? prev_1.capabilities.filter(cap => cap !== capability) : [...prev_1.capabilities, capability]
    }));
  };
  const clearFilters = () => {
    setFilters({
      providers: [],
      capabilities: [],
      priceRange: "all",
      hideUnstable: true,
      showOnlyRoot: false,
      showFavoritesOnly: false,
      showOnlyWithKeys: false
    });
  };
  const hasActiveFilters = filters.providers.length > 0 || filters.capabilities.length > 0 || filters.priceRange !== "all" || !filters.hideUnstable || filters.showOnlyRoot || filters.showFavoritesOnly || filters.showOnlyWithKeys;
  const getProviderLogo = (providerId_0: ProviderId, compact = false) => {
    const LogoComponent = providerId_0 === "openai" ? OpenAIMarkIcon : providerLogoUrls[providerId_0];
    if (LogoComponent) {
      if (compact) {
        return <div className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-sm">
						<LogoComponent className="h-[22px] w-auto max-w-[26px] shrink-0 object-contain" />
					</div>;
      }
      return <LogoComponent className="h-10 w-10 object-contain" />;
    }
    const IconComponent = getProviderIcon(providerId_0);
    if (IconComponent) {
      if (compact) {
        return <div className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-sm">
						<IconComponent className="h-[22px] w-auto max-w-[26px] shrink-0 object-contain" />
					</div>;
      }
      return <IconComponent className="h-10 w-10 object-contain" />;
    }
    if (compact) {
      return <div className="size-7 shrink-0 rounded-sm bg-gray-200" />;
    }
    return <div className="h-10 w-10 rounded bg-gray-200" />;
  };

  // Keep desktop preview in sync with the currently selected model when opening
  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (!selectedModel) {
      setPreviewEntry(null);
      return;
    }

    // Prefer provider-specific entry when a provider is selected.
    // Match on region to distinguish regional variants.
    let entry_0 = selectedProviderId && allEntries.find(e_9 => !e_9.isRoot && e_9.model.id === selectedModel.id && e_9.mapping?.providerId === selectedProviderId && (!selectedMapping || e_9.mapping?.region === selectedMapping.region));

    // Fallback to root entry for the selected model
    entry_0 ??= allEntries.find(e => e.isRoot && e.model.id === selectedModel.id);

    // Fallback to first filtered entry
    if (!entry_0 && filteredEntries.length > 0) {
      entry_0 = filteredEntries[0];
    }
    setPreviewEntry(entry_0 ? {
      model: entry_0.model,
      mapping: entry_0.mapping,
      provider: entry_0.provider,
      isRoot: entry_0.isRoot
    } : null);
  }, [open, selectedModel, selectedProviderId, selectedMapping, allEntries, filteredEntries]);
  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [open, searchQuery]);
  const handleInputKeyDown = React.useCallback((e_10: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_10.key === "ArrowDown") {
      e_10.preventDefault();
      e_10.stopPropagation();
      const next = Math.min(focusedIndex + 1, filteredEntries.length - 1);
      setFocusedIndex(next);
      const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
      scrollEl?.scrollTo({
        top: next * ROW_HEIGHT
      });
    } else if (e_10.key === "ArrowUp") {
      e_10.preventDefault();
      e_10.stopPropagation();
      const prev_2 = Math.max(focusedIndex - 1, 0);
      setFocusedIndex(prev_2);
      const scrollEl_0 = listContainerRef.current?.firstElementChild as HTMLElement | null;
      scrollEl_0?.scrollTo({
        top: prev_2 * ROW_HEIGHT
      });
    } else if (e_10.key === "Enter") {
      e_10.preventDefault();
      e_10.stopPropagation();
      const entry_1 = filteredEntries[focusedIndex];
      if (!entry_1) {
        return;
      }
      const {
        model: model_0,
        mapping,
        isRoot
      } = entry_1;
      const value_1 = isRoot ? model_0.id : `${mapping!.providerId}/${model_0.id}${mapping!.region ? `:${mapping!.region}` : ""}`;
      const disabled = isRoot ? isOptionDisabled?.(model_0.id) ?? false : isOptionDisabled?.(value_1) ?? false;
      if (disabled) {
        return;
      }
      onValueChange?.(value_1);
      setOpen(false);
    }
  }, [focusedIndex, filteredEntries, isOptionDisabled, onValueChange]);
  const rowProps = React.useMemo(() => ({
    entries: filteredEntries,
    focusedIndex,
    setFocusedIndex,
    selectedEntryKey,
    isFavorite,
    toggleFavorite,
    onSelect: (value_2: string) => {
      onValueChange?.(value_2);
      setOpen(false);
    },
    setPreviewEntry,
    setSelectedDetails,
    setDetailsOpen,
    isOptionDisabled,
    getOptionDisabledReason
  }), [filteredEntries, focusedIndex, selectedEntryKey, isFavorite, toggleFavorite, onValueChange, isOptionDisabled, getOptionDisabledReason]);
  return <>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button variant="ghost" role="combobox" aria-expanded={open} className={cn("h-[38px] w-full justify-between px-3 sm:px-4", compactTrigger && "h-8 w-auto max-w-full min-w-0 justify-start gap-2 px-0 text-[0.94rem] font-semibold hover:bg-transparent")}>
						{selectedModel ? <div className={cn("flex min-w-0 flex-1 items-center gap-2 sm:gap-3", compactTrigger && "w-auto flex-none gap-1.5 sm:gap-2")}>
								{(() => {
              if (selectedModelId === selectedModel.id && !selectedProviderDef) {
                return <Sparkles className={cn("shrink-0 text-primary", compactTrigger ? "h-[22px] w-[22px]" : "h-10 w-10")} />;
              }
              return getProviderLogo((selectedProviderId || selectedModel.mappings[0].providerId) as ProviderId, compactTrigger);
            })()}
								<div className={cn("flex min-w-0 flex-1 flex-col items-start", compactTrigger && "w-auto flex-none justify-center")}>
									<div className="flex max-w-full items-center gap-1">
										<span className={cn("truncate font-medium", compactTrigger && "text-[0.92rem] font-semibold")}>
											{selectedModel.name}
										</span>
										{(() => {
                  if (compactTrigger) {
                    return null;
                  }
                  const mappingForWarning = selectedModel.mappings.find(p_4 => p_4.providerId === selectedProviderId);
                  const isUnstable = mappingForWarning && isModelUnstable(mappingForWarning, selectedModel);
                  const isDeprecated = mappingForWarning?.deprecatedAt && new Date(mappingForWarning.deprecatedAt) <= new Date();
                  return isUnstable || isDeprecated ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-yellow-600 dark:text-yellow-500" /> : null;
                })()}
									</div>
									<span className={cn("max-w-full truncate text-xs text-muted-foreground", compactTrigger && "hidden")}>
										{selectedModelId === selectedModel.id && !selectedProviderDef ? requiresProviderSelection ? "Select a provider" : "Auto-select provider" : (selectedProviderDef ?? getProviderForModel(selectedModel, providers))?.name}
										{selectedMapping?.region && <span className="ml-1">({selectedMapping.region})</span>}
									</span>
								</div>
							</div> : <span className="truncate">{placeholder}</span>}
						<ChevronsUpDown className={cn("ml-2 h-4 w-4 shrink-0 opacity-50", compactTrigger && "ml-0.5 h-3.5 w-3.5 opacity-65")} />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="z-99999 w-[300px] p-0 sm:w-[720px]" sideOffset={4} align="start">
					<div className="flex w-[300px] md:w-full">
						{/* Main content - model list & filters */}
						<div className="flex-1 w-[300px] md:w-[340px]">
							<Command shouldFilter={false}>
								<div className="flex w-[300px] items-center border-b px-3 md:w-full [&_[cmdk-input-wrapper]]:border-0">
									<CommandInput placeholder="Search models..." value={searchQuery} onValueChange={setSearchQuery} onKeyDown={handleInputKeyDown} className="h-[38px]" />
									<Popover open={filterOpen} onOpenChange={setFilterOpen}>
										<PopoverTrigger asChild>
											<Button variant="ghost" size="sm" className={cn("ml-2 h-8 w-8 p-0", hasActiveFilters && "text-primary")}>
												<Filter className="h-4 w-4" />
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 h-[400px] overflow-y-scroll md:h-full" style={{
                    zIndex: 100000
                  }} side={isMobile ? "bottom" : "right"} align={isMobile ? "end" : "start"}>
											<div className="space-y-3">
												<div className="flex items-center justify-between">
													<h4 className="font-medium">Filters</h4>
													{hasActiveFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>
															Clear all
														</Button>}
												</div>

												{/* Toggle filters group */}
												<div className="space-y-2">
													<div className="flex items-center justify-between">
														<Label htmlFor="show-root" className="text-sm cursor-pointer font-medium">
															Show only root models
														</Label>
														<Switch id="show-root" checked={filters.showOnlyRoot} onCheckedChange={checked => updateFilter("showOnlyRoot", checked)} />
													</div>
													<div className="flex items-center justify-between">
														<Label htmlFor="show-favorites" className="text-sm cursor-pointer font-medium">
															Show favorites only
														</Label>
														<Switch id="show-favorites" checked={filters.showFavoritesOnly} onCheckedChange={checked_0 => updateFilter("showFavoritesOnly", checked_0)} />
													</div>
													{providersWithKeys.size > 0 && <div className="flex items-center justify-between">
															<Label htmlFor="show-with-keys" className="text-sm cursor-pointer font-medium">
																Show only providers with keys
															</Label>
															<Switch id="show-with-keys" checked={filters.showOnlyWithKeys} onCheckedChange={checked_1 => updateFilter("showOnlyWithKeys", checked_1)} />
														</div>}
												</div>

												<Separator />

												{/* Provider filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														Providers
													</Label>
													<div className="space-y-1.5 max-h-28 overflow-y-auto">
														{availableProviders.map(provider_0 => {
                            const ProviderIcon = getProviderIcon(provider_0.id);
                            return <div key={provider_0.id} className="flex items-center space-x-2">
																	<Checkbox id={`provider-${provider_0.id}`} checked={filters.providers.includes(provider_0.id)} onCheckedChange={() => toggleProviderFilter(provider_0.id)} />
																	<Label htmlFor={`provider-${provider_0.id}`} className="flex items-center gap-2 text-sm cursor-pointer">
																		{ProviderIcon && <ProviderIcon className="h-3 w-3" style={{
                                  color: provider_0.color ?? undefined
                                }} />}
																		{provider_0.name}
																	</Label>
																</div>;
                          })}
													</div>
												</div>

												<Separator />

												{/* Capabilities filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														Capabilities
													</Label>
													<div className="space-y-1.5 max-h-28 overflow-y-auto">
														{availableCapabilities.map(capability_0 => <div key={capability_0} className="flex items-center space-x-2">
																<Checkbox id={`capability-${capability_0}`} checked={filters.capabilities.includes(capability_0)} onCheckedChange={() => toggleCapabilityFilter(capability_0)} />
																<Label htmlFor={`capability-${capability_0}`} className="text-sm cursor-pointer">
																	{capability_0}
																</Label>
															</div>)}
													</div>
												</div>

												<Separator />

												{/* Price range filter */}
												<div className="space-y-1.5">
													<Label className="text-sm font-medium">
														Price Range
													</Label>
													<div className="space-y-1.5">
														{[{
                            value: "all",
                            label: "All models"
                          }, {
                            value: "free",
                            label: "Free models"
                          }, {
                            value: "low",
                            label: "Low cost (≤ $0.000001)"
                          }, {
                            value: "medium",
                            label: "Medium cost (≤ $0.00001)"
                          }, {
                            value: "high",
                            label: "High cost (> $0.00001)"
                          }].map(option => <div key={option.value} className="flex items-center space-x-2">
																<Checkbox id={`price-${option.value}`} checked={filters.priceRange === option.value} onCheckedChange={() => updateFilter("priceRange", option.value)} />
																<Label htmlFor={`price-${option.value}`} className="text-sm cursor-pointer">
																	{option.label}
																</Label>
															</div>)}
													</div>
												</div>

												<Separator />

												{/* Stability filter */}
												<div className="flex items-center justify-between">
													<Label htmlFor="hide-unstable" className="text-sm cursor-pointer font-medium">
														Hide unstable/experimental
													</Label>
													<Switch id="hide-unstable" checked={filters.hideUnstable} onCheckedChange={checked_2 => updateFilter("hideUnstable", checked_2)} />
												</div>
											</div>
										</PopoverContent>
									</Popover>
								</div>
								<div>
									<div className="px-3 py-1 text-xs text-muted-foreground">
										{filteredEntries.length} model
										{filteredEntries.length !== 1 ? "s" : ""} found
									</div>
									{filteredEntries.length === 0 ? <div className="py-6 text-center text-sm">
											No models found.
											{hasActiveFilters && <Button variant="link" size="sm" onClick={clearFilters} className="mt-2 block mx-auto">
													Clear filters to see all models
												</Button>}
										</div> : <div ref={listContainerRef} className="h-[300px] sm:h-[400px]">
											<List style={{
                    height: "100%",
                    width: "100%"
                  }} rowComponent={ModelEntryRowComponent} rowCount={filteredEntries.length} rowHeight={ROW_HEIGHT} rowProps={rowProps} overscanCount={8} />
										</div>}
								</div>
							</Command>
						</div>
						{/* Desktop preview panel */}
						<div className="hidden md:block w-[360px] border-l border-border bg-muted/40">
							<div className="p-4 space-y-3 h-full overflow-y-auto">
								{previewEntry ? <>
										<div className="flex items-center gap-3">
											{(() => {
                    if (!previewEntry.provider) {
                      return <div className="p-2 rounded-lg bg-primary/10">
															<Sparkles className="h-5 w-5 text-primary" />
														</div>;
                    }
                    const ProviderIcon_0 = getProviderIcon(previewEntry.provider.id);
                    return ProviderIcon_0 ? <div className="p-2 rounded-lg" style={{
                      backgroundColor: `${previewEntry.provider?.color}15`
                    }}>
														<ProviderIcon_0 className="h-5 w-5 dark:text-white" />
													</div> : null;
                  })()}
											<div className="flex-1 min-w-0">
												<div className="font-semibold text-sm truncate">
													{previewEntry.model.name}
												</div>
												<div className="text-xs text-muted-foreground truncate">
													{previewEntry.provider?.name ?? "Auto-select provider"}
													{previewEntry.mapping?.region && <span className="ml-1">
															({previewEntry.mapping.region})
														</span>}
												</div>
												<div className="text-[11px] text-muted-foreground capitalize truncate">
													{previewEntry.model.id !== "auto" ? `${previewEntry.model.family} family` : "-"}
												</div>
											</div>
										</div>

										{!previewEntry.provider ? <>
												<p className="text-xs text-muted-foreground leading-relaxed">
													This is a root model ID. The Gateway will
													automatically select the best provider for this model
													based on availability, performance, and cost. Specific
													capabilities and pricing will depend on the selected
													provider.
												</p>

												{(() => {
                    const aggregate = getRootAggregateInfo(previewEntry.model);
                    const isVideo = mode === "video" || !!previewEntry.model.output?.includes("video");
                    const minPerSec = isVideo ? getMinPerSecondPrice(previewEntry.model.mappings) : null;
                    const maxPerSec = isVideo ? getMaxPerSecondPrice(previewEntry.model.mappings) : null;
                    const hasPricingOrLimits = isVideo ? minPerSec !== null : aggregate.minInputPrice !== undefined || aggregate.minOutputPrice !== undefined || aggregate.maxContextSize !== undefined || aggregate.maxOutput !== undefined;
                    const hasImagePricing = !isVideo && (aggregate.minRequestPrice !== undefined && aggregate.minRequestPrice > 0 || aggregate.minImageInputPrice !== undefined || aggregate.minImageOutputPrice !== undefined);
                    const imageEstimate = mode === "image" ? getMinImageCostEstimate(previewEntry.model.mappings) : null;
                    const hasCapabilities = aggregate.capabilities.length > 0;
                    if (!hasPricingOrLimits && !hasImagePricing && !hasCapabilities && !imageEstimate) {
                      return null;
                    }
                    return <div className="space-y-3 pt-3 border-t border-dashed">
															{imageEstimate && <ImageEstimateCard estimate={imageEstimate} />}
															{hasPricingOrLimits && (isVideo ? <div className="space-y-2">
																		<h5 className="font-medium text-xs">
																			Video Pricing{" "}
																			<span className="text-[11px] font-normal text-muted-foreground">
																				(per second)
																			</span>
																		</h5>
																		<div className="grid grid-cols-2 gap-3">
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					From
																				</span>
																				<p className="text-xs font-mono">
																					{minPerSec ?? "Unknown"}
																				</p>
																			</div>
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					Up to
																				</span>
																				<p className="text-xs font-mono">
																					{maxPerSec ?? "Unknown"}
																				</p>
																			</div>
																		</div>
																		{mode === "chat" && <div className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
																				<Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
																				<span>
																					Selecting this model will open Video
																					Studio.
																				</span>
																			</div>}
																	</div> : <div className="space-y-2">
																		<h5 className="font-medium text-xs">
																			Pricing &amp; Limits{" "}
																			<span className="text-[11px] font-normal text-muted-foreground">
																				(starts at)
																			</span>
																		</h5>
																		<div className="grid grid-cols-2 gap-3">
																			{(!previewIsImageGen || previewExpandTokens) && <>
																					<div className="space-y-1">
																						<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																							Input
																						</span>
																						<p className="text-xs font-mono">
																							{aggregate.minInputPrice !== undefined ? formatPrice(aggregate.minInputPrice) : "Unknown"}
																						</p>
																					</div>
																					<div className="space-y-1">
																						<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																							Output
																						</span>
																						<p className="text-xs font-mono">
																							{aggregate.minOutputPrice !== undefined ? formatPrice(aggregate.minOutputPrice) : "Unknown"}
																						</p>
																					</div>
																				</>}
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					Context
																				</span>
																				<p className="text-xs font-mono">
																					{formatContextSize(aggregate.maxContextSize)}
																				</p>
																			</div>
																			<div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					Max Output
																				</span>
																				<p className="text-xs font-mono">
																					{formatContextSize(aggregate.maxOutput)}
																				</p>
																			</div>
																		</div>
																		{previewIsImageGen && <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setPreviewExpandTokens(v => !v)}>
																				{previewExpandTokens ? "Hide token pricing" : "Expand details"}
																			</button>}
																	</div>)}

															{hasImagePricing && <div className="pt-2">
																	<div className="grid grid-cols-2 gap-3">
																		{aggregate.minRequestPrice !== undefined && aggregate.minRequestPrice > 0 && <div className="space-y-1">
																					<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																						Per Request
																					</span>
																					<p className="text-xs font-mono">
																						$
																						{aggregate.minRequestPrice.toFixed(3)}
																						/req
																					</p>
																				</div>}
																		{aggregate.minImageInputPrice !== undefined && <div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					Image Input
																				</span>
																				<p className="text-xs font-mono">
																					{formatPrice(aggregate.minImageInputPrice)}
																				</p>
																			</div>}
																		{aggregate.minImageOutputPrice !== undefined && <div className="space-y-1">
																				<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																					Image Output
																				</span>
																				<p className="text-xs font-mono">
																					{formatPrice(aggregate.minImageOutputPrice)}
																				</p>
																			</div>}
																	</div>
																</div>}

															{hasCapabilities && previewEntry.model.id !== "auto" && <div className="space-y-1">
																		<h5 className="font-medium text-xs">
																			Capabilities
																		</h5>
																		<div className="flex flex-wrap gap-1">
																			{aggregate.capabilities.map(capability_1 => {
                            const {
                              Icon
                            } = getCapabilityIconConfig(capability_1);
                            return <Badge key={capability_1} variant="secondary" className="text-[10px] px-1.5 py-0.5 flex items-center gap-1">
																							{Icon && <Icon size={12} />}
																							{capability_1}
																						</Badge>;
                          })}
																		</div>
																	</div>}
														</div>;
                  })()}
											</> : <>
												{previewEntry.provider?.description && <p className="text-xs text-muted-foreground leading-relaxed">
														{previewEntry.provider.description}
													</p>}

												<div className="space-y-2">
													<h5 className="font-medium text-xs">
														{mode === "video" || previewEntry.model.output?.includes("video") ? "Video Pricing" : "Pricing & Limits"}
													</h5>
													{mode === "video" || previewEntry.model.output?.includes("video") ? <>
															<div className="grid grid-cols-1 gap-3">
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		Per Second
																	</span>
																	<p className="text-xs font-mono">
																		{previewEntry.mapping?.perSecondPrice ? formatPerSecondPrice(previewEntry.mapping.perSecondPrice) : "Unknown"}
																	</p>
																</div>
															</div>
															{mode === "chat" && <div className="flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
																	<Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
																	<span>
																		Selecting this model will open Video Studio.
																	</span>
																</div>}
														</> : <>
															<div className="grid grid-cols-2 gap-3">
																{(!previewIsImageGen || previewExpandTokens) && <>
																		<div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				Input
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                                const price_0 = getMappingPriceInfo(previewEntry.mapping, "input");
                                if (price_0.original && price_0.discounted && price_0.original !== price_0.discounted) {
                                  return <>
																								<span className="line-through text-muted-foreground">
																									{price_0.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price_0.discounted}
																								</span>
																							</>;
                                }
                                return price_0.label;
                              })()}
																			</p>
																		</div>
																		<div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				Output
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                                const price_1 = getMappingPriceInfo(previewEntry.mapping, "output");
                                if (price_1.original && price_1.discounted && price_1.original !== price_1.discounted) {
                                  return <>
																								<span className="line-through text-muted-foreground">
																									{price_1.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price_1.discounted}
																								</span>
																							</>;
                                }
                                return price_1.label;
                              })()}
																			</p>
																		</div>
																	</>}
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		Context
																	</span>
																	<p className="text-xs font-mono">
																		{formatContextSize(previewEntry.mapping?.contextSize)}
																	</p>
																</div>
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		Max Output
																	</span>
																	<p className="text-xs font-mono">
																		{formatContextSize(previewEntry.mapping?.maxOutput)}
																	</p>
																</div>
															</div>
															{previewIsImageGen && <button type="button" className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setPreviewExpandTokens(v_0 => !v_0)}>
																	{previewExpandTokens ? "Hide token pricing" : "Expand details"}
																</button>}
														</>}
													{previewEntry.mapping?.cachedInputPrice && (!previewIsImageGen || previewExpandTokens) && <div className="pt-2 border-t border-dashed">
																<div className="space-y-1">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		Cached Input
																	</span>
																	<p className="text-xs font-mono text-green-600 dark:text-green-400">
																		{(() => {
                            const price_2 = getMappingPriceInfo(previewEntry.mapping, "cachedInput");
                            if (price_2.original && price_2.discounted && price_2.original !== price_2.discounted) {
                              return <>
																						<span className="line-through text-muted-foreground">
																							{price_2.original}
																						</span>{" "}
																						<span className="text-green-500">
																							{price_2.discounted}
																						</span>
																					</>;
                            }
                            return price_2.label;
                          })()}
																	</p>
																</div>
															</div>}
													{mode === "image" && (() => {
                      const est = estimateImageCost(previewEntry.mapping);
                      if (!est) {
                        return null;
                      }
                      return <div className="pt-2 mt-2 rounded-md border border-primary/30 bg-primary/5 p-2.5">
																	<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																		Est. per Image
																	</span>
																	<p className="text-sm font-mono font-semibold text-primary">
																		~{formatImageCost(est.discounted)}
																	</p>
																	<p className="text-[10px] text-muted-foreground">
																		Typical 1024×1024 output
																	</p>
																</div>;
                    })()}
													{/* Image Generation Pricing */}
													{(previewEntry.mapping?.requestPrice ?? previewEntry.mapping?.imageInputPrice) && <div className="pt-2">
															<div className="grid grid-cols-2 gap-3">
																{previewEntry.mapping?.requestPrice && parseFloat(previewEntry.mapping.requestPrice) > 0 && <div className="space-y-1">
																			<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																				Per Request
																			</span>
																			<p className="text-xs font-mono">
																				{(() => {
                              const price_3 = getMappingPriceInfo(previewEntry.mapping, "request");
                              if (price_3.original && price_3.discounted && price_3.original !== price_3.discounted) {
                                return <>
																								<span className="line-through text-muted-foreground">
																									{price_3.original}
																								</span>{" "}
																								<span className="text-green-500">
																									{price_3.discounted}
																								</span>
																							</>;
                              }
                              return price_3.label;
                            })()}
																			</p>
																		</div>}
																{previewEntry.mapping?.imageInputPrice !== undefined && <div className="space-y-1">
																		<span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
																			Image Input
																		</span>
																		<p className="text-xs font-mono">
																			{(() => {
                              const price_4 = getMappingPriceInfo(previewEntry.mapping, "imageInput");
                              if (price_4.original && price_4.discounted && price_4.original !== price_4.discounted) {
                                return <>
																							<span className="line-through text-muted-foreground">
																								{price_4.original}
																							</span>{" "}
																							<span className="text-green-500">
																								{price_4.discounted}
																							</span>
																						</>;
                              }
                              return price_4.label;
                            })()}
																		</p>
																	</div>}
															</div>
														</div>}
												</div>

												{(() => {
                    const caps_0 = getMappingCapabilities(previewEntry.mapping, previewEntry.model);
                    return caps_0.length > 0 ? <div className="space-y-1">
															<h5 className="font-medium text-xs">
																Capabilities
															</h5>
															<div className="flex flex-wrap gap-1">
																{caps_0.map(capability_2 => {
                          const {
                            Icon: Icon_0
                          } = getCapabilityIconConfig(capability_2);
                          return <Badge key={capability_2} variant="secondary" className="text-[10px] px-1.5 py-0.5 flex items-center gap-1">
																			{Icon_0 && <Icon_0 size={12} />}
																			{capability_2}
																		</Badge>;
                        })}
															</div>
														</div> : null;
                  })()}
												{(() => {
                    if (!previewEntry.mapping) {
                      return null;
                    }
                    const regions = previewEntry.model.mappings.filter(m_3 => m_3.providerId === previewEntry.mapping!.providerId && m_3.region).map(m_4 => m_4.region as string);
                    return regions.length > 0 ? <div className="space-y-1">
															<h5 className="font-medium text-xs">
																Available Regions
															</h5>
															<div className="flex flex-wrap gap-1">
																{regions.map(r => <Badge key={r} variant="secondary" className="text-[10px] px-1.5 py-0.5">
																		{r}
																	</Badge>)}
															</div>
														</div> : null;
                  })()}
											</>}
									</> : <p className="text-xs text-muted-foreground">
										Hover a model to see details.
									</p>}
							</div>
						</div>
					</div>
				</PopoverContent>
			</Popover>

			{/* Model Details Dialog */}
			<Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
				<DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
					{selectedDetails && <>
							<DialogHeader>
								<DialogTitle className="flex items-center gap-3">
									{(() => {
                if (!selectedDetails.provider) {
                  return <div className="p-2 rounded-lg bg-primary/10">
													<Sparkles className="h-6 w-6 text-primary" />
												</div>;
                }
                const ProviderIcon_1 = getProviderIcon(selectedDetails.provider.id);
                return ProviderIcon_1 ? <div className="p-2 rounded-lg" style={{
                  backgroundColor: `${selectedDetails.provider?.color}15`
                }}>
												<ProviderIcon_1 className="h-6 w-6 dark:text-white" />
											</div> : null;
              })()}
									<div className="flex-1">
										<div className="font-semibold text-base">
											{selectedDetails.model.name}
										</div>
										<div className="text-sm text-muted-foreground font-normal">
											{selectedDetails.provider?.name ?? "Auto-select provider"}
											{selectedDetails.mapping?.region && <span className="ml-1">
													({selectedDetails.mapping.region})
												</span>}
										</div>
										<div className="text-xs text-muted-foreground font-normal capitalize">
											{selectedDetails.model.family} family
										</div>
									</div>
									{selectedDetails.provider?.website && <Button variant="ghost" size="sm" asChild>
											<a href={selectedDetails.provider.website} target="_blank" rel="noopener noreferrer" className="h-8 w-8 p-0">
												<ExternalLink className="h-3 w-3" />
											</a>
										</Button>}
								</DialogTitle>
							</DialogHeader>

							<div className="space-y-4">
								{!selectedDetails.provider ? <div className="space-y-4">
										<p className="text-sm text-muted-foreground leading-relaxed">
											This is a root model ID. The Gateway will automatically
											select the best provider for this model based on
											availability, performance, and cost. Specific capabilities
											and pricing will depend on the selected provider.
										</p>

										{(() => {
                const aggregate_0 = getRootAggregateInfo(selectedDetails.model);
                const imageEstimate_0 = getMinImageCostEstimate(selectedDetails.model.mappings);
                const hasPricingOrLimits_0 = aggregate_0.minInputPrice !== undefined || aggregate_0.minOutputPrice !== undefined || aggregate_0.maxContextSize !== undefined || aggregate_0.maxOutput !== undefined;
                const hasImagePricing_0 = aggregate_0.minRequestPrice !== undefined && aggregate_0.minRequestPrice > 0 || aggregate_0.minImageInputPrice !== undefined || aggregate_0.minImageOutputPrice !== undefined;
                const hasCapabilities_0 = aggregate_0.capabilities.length > 0;
                if (!hasPricingOrLimits_0 && !hasImagePricing_0 && !hasCapabilities_0 && !imageEstimate_0) {
                  return null;
                }
                return <div className="space-y-4">
													{imageEstimate_0 && <ImageEstimateCard estimate={imageEstimate_0} />}
													{hasPricingOrLimits_0 && <div className="space-y-3">
															<h5 className="font-medium text-sm">
																Pricing &amp; Limits{" "}
																<span className="text-xs font-normal text-muted-foreground">
																	(starts at)
																</span>
															</h5>
															<div className="grid grid-cols-2 gap-3">
																{(!selectedIsImageGen || selectedExpandTokens) && <>
																		<div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				Input
																			</span>
																			<p className="text-sm font-mono">
																				{aggregate_0.minInputPrice !== undefined ? formatPrice(aggregate_0.minInputPrice) : "Unknown"}
																			</p>
																		</div>
																		<div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				Output
																			</span>
																			<p className="text-sm font-mono">
																				{aggregate_0.minOutputPrice !== undefined ? formatPrice(aggregate_0.minOutputPrice) : "Unknown"}
																			</p>
																		</div>
																	</>}
																<div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		Context
																	</span>
																	<p className="text-sm font-mono">
																		{formatContextSize(aggregate_0.maxContextSize)}
																	</p>
																</div>
																<div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		Max Output
																	</span>
																	<p className="text-sm font-mono">
																		{formatContextSize(aggregate_0.maxOutput)}
																	</p>
																</div>
															</div>
															{selectedIsImageGen && <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setSelectedExpandTokens(v_1 => !v_1)}>
																	{selectedExpandTokens ? "Hide token pricing" : "Expand details"}
																</button>}
														</div>}

													{hasImagePricing_0 && <div className="pt-2">
															<div className="grid grid-cols-2 gap-3">
																{aggregate_0.minRequestPrice !== undefined && aggregate_0.minRequestPrice > 0 && <div className="space-y-1">
																			<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																				Per Request
																			</span>
																			<p className="text-sm font-mono">
																				${aggregate_0.minRequestPrice.toFixed(3)}
																				/req
																			</p>
																		</div>}
																{aggregate_0.minImageInputPrice !== undefined && <div className="space-y-1">
																		<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																			Image Input
																		</span>
																		<p className="text-sm font-mono">
																			{formatPrice(aggregate_0.minImageInputPrice)}
																		</p>
																	</div>}
																{aggregate_0.minImageOutputPrice !== undefined && <div className="space-y-1">
																		<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																			Image Output
																		</span>
																		<p className="text-sm font-mono">
																			{formatPrice(aggregate_0.minImageOutputPrice)}
																		</p>
																	</div>}
															</div>
														</div>}

													{hasCapabilities_0 && <div className="space-y-2">
															<h5 className="font-medium text-sm">
																Capabilities
															</h5>
															<div className="flex flex-wrap gap-1.5">
																{aggregate_0.capabilities.map(capability_3 => <Badge key={capability_3} variant="secondary" className="text-xs px-2 py-1">
																		{capability_3}
																	</Badge>)}
															</div>
														</div>}
												</div>;
              })()}
									</div> : <>
										{selectedDetails.provider?.description && <>
												<p className="text-sm text-muted-foreground leading-relaxed">
													{selectedDetails.provider.description}
												</p>
												<Separator />
											</>}

										{(() => {
                const imageEstimate_1 = selectedDetails.mapping ? estimateImageCost(selectedDetails.mapping) : null;
                return imageEstimate_1 ? <ImageEstimateCard estimate={imageEstimate_1} /> : null;
              })()}

										<div className="space-y-3">
											<h5 className="font-medium text-sm">Pricing & Limits</h5>
											<div className="grid grid-cols-2 gap-3">
												{(!selectedIsImageGen || selectedExpandTokens) && <>
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																Input
															</span>
															<p className="text-sm font-mono">
																{(() => {
                          const price_5 = getMappingPriceInfo(selectedDetails.mapping, "input");
                          if (price_5.original && price_5.discounted && price_5.original !== price_5.discounted) {
                            return <>
																				<span className="line-through text-muted-foreground">
																					{price_5.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_5.discounted}
																				</span>
																			</>;
                          }
                          return price_5.label;
                        })()}
															</p>
														</div>
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																Output
															</span>
															<p className="text-sm font-mono">
																{(() => {
                          const price_6 = getMappingPriceInfo(selectedDetails.mapping, "output");
                          if (price_6.original && price_6.discounted && price_6.original !== price_6.discounted) {
                            return <>
																				<span className="line-through text-muted-foreground">
																					{price_6.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_6.discounted}
																				</span>
																			</>;
                          }
                          return price_6.label;
                        })()}
															</p>
														</div>
													</>}
												<div className="space-y-1">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
														Context
													</span>
													<p className="text-sm font-mono">
														{formatContextSize(selectedDetails.mapping?.contextSize)}
													</p>
												</div>
												<div className="space-y-1">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
														Max Output
													</span>
													<p className="text-sm font-mono">
														{formatContextSize(selectedDetails.mapping?.maxOutput)}
													</p>
												</div>
											</div>
											{selectedIsImageGen && <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline" onClick={() => setSelectedExpandTokens(v_2 => !v_2)}>
													{selectedExpandTokens ? "Hide token pricing" : "Expand details"}
												</button>}
											{selectedDetails.mapping?.cachedInputPrice && (!selectedIsImageGen || selectedExpandTokens) && <div className="pt-2 border-t border-dashed">
														<div className="space-y-1">
															<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																Cached Input
															</span>
															<p className="text-sm font-mono text-green-600 dark:text-green-400">
																{(() => {
                        const price_7 = getMappingPriceInfo(selectedDetails.mapping, "cachedInput");
                        if (price_7.original && price_7.discounted && price_7.original !== price_7.discounted) {
                          return <>
																				<span className="line-through text-muted-foreground">
																					{price_7.original}
																				</span>{" "}
																				<span className="text-green-500">
																					{price_7.discounted}
																				</span>
																			</>;
                        }
                        return price_7.label;
                      })()}
															</p>
														</div>
													</div>}
											{/* Image Generation Pricing */}
											{(selectedDetails.mapping?.requestPrice ?? selectedDetails.mapping?.imageInputPrice) && <div className="pt-2 border-t border-dashed">
													<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-2">
														Image Pricing
													</span>
													<div className="grid grid-cols-2 gap-3">
														{selectedDetails.mapping?.requestPrice && parseFloat(selectedDetails.mapping.requestPrice) > 0 && <div className="space-y-1">
																	<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																		Per Request
																	</span>
																	<p className="text-sm font-mono">
																		{(() => {
                          const price_8 = getMappingPriceInfo(selectedDetails.mapping, "request");
                          if (price_8.original && price_8.discounted && price_8.original !== price_8.discounted) {
                            return <>
																						<span className="line-through text-muted-foreground">
																							{price_8.original}
																						</span>{" "}
																						<span className="text-green-500">
																							{price_8.discounted}
																						</span>
																					</>;
                          }
                          return price_8.label;
                        })()}
																	</p>
																</div>}
														{selectedDetails.mapping?.imageInputPrice !== undefined && <div className="space-y-1">
																<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
																	Image Input
																</span>
																<p className="text-sm font-mono">
																	{(() => {
                          const price_9 = getMappingPriceInfo(selectedDetails.mapping, "imageInput");
                          if (price_9.original && price_9.discounted && price_9.original !== price_9.discounted) {
                            return <>
																					<span className="line-through text-muted-foreground">
																						{price_9.original}
																					</span>{" "}
																					<span className="text-green-500">
																						{price_9.discounted}
																					</span>
																				</>;
                          }
                          return price_9.label;
                        })()}
																</p>
															</div>}
													</div>
												</div>}
										</div>

										<Separator />

										{(() => {
                const caps_1 = getMappingCapabilities(selectedDetails.mapping, selectedDetails.model);
                return caps_1.length > 0 ? <div className="space-y-2">
													<h5 className="font-medium text-sm">Capabilities</h5>
													<div className="flex flex-wrap gap-1.5">
														{caps_1.map(capability_4 => {
                      const {
                        Icon: Icon_1
                      } = getCapabilityIconConfig(capability_4);
                      return <Badge key={capability_4} variant="secondary" className="text-xs px-2 py-1 flex items-center gap-1.5">
																	{Icon_1 && <Icon_1 size={14} />}
																	{capability_4}
																</Badge>;
                    })}
													</div>
												</div> : null;
              })()}
										{(() => {
                if (!selectedDetails.mapping) {
                  return null;
                }
                const regions_0 = selectedDetails.model.mappings.filter(m_5 => m_5.providerId === selectedDetails.mapping!.providerId && m_5.region).map(m_6 => m_6.region as string);
                return regions_0.length > 0 ? <div className="space-y-2">
													<h5 className="font-medium text-sm">
														Available Regions
													</h5>
													<div className="flex flex-wrap gap-1.5">
														{regions_0.map(r_0 => <Badge key={r_0} variant="secondary" className="text-xs px-2 py-1">
																{r_0}
															</Badge>)}
													</div>
												</div> : null;
              })()}
									</>}
							</div>
						</>}
				</DialogContent>
			</Dialog>
		</>;
}